/**
 * Erwal — bedenktijd voor het uitklapmenu, twee kanten op, in de juiste
 * volgorde.
 *
 * SLUITEN: Horizon sluit een open uitklappaneel ONMIDDELLIJK bij het eerste
 * pointerleave op het menu-item. Elke rimpeling — de strook tussen balk en
 * zwevend paneel, een trillende muis, een overlay van een browserextensie —
 * klapt het menu daardoor dicht terwijl je er nog naartoe onderweg bent.
 * Dit script vangt dat pointerleave af vóór Horizon het ziet en geeft het
 * pas na een korte genadeperiode door.
 *
 * OPENEN: Horizon opent óók onmiddellijk, bij pointerenter. Wie met de muis
 * dwars over de menubalk beweegt, trok zo een spoor van openklappende
 * panelen. Daarom rust de muis eerst even op het item voor het paneel
 * opengaat. Strijken telt niet, wijzen wel.
 *
 * VOLGORDE (de flikkerbug van 31 aug): een uitgestelde pointerleave van
 * item A kon ná de activate van buurman B bij Horizon aankomen. Horizon
 * verwerkte dat als "sluit" terwijl B net open was — het verse paneel
 * klapte direct weer dicht ("ik hover en hij sluit", klacht klant).
 * Daarom spoelt elke uitgestelde opening eerst álle hangende
 * sluit-events door (synchroon, in de natuurlijke volgorde leave → enter)
 * voordat de enter wordt doorgegeven. Horizon ziet zo nooit meer events
 * in de verkeerde volgorde.
 *
 * Waarom op WINDOW en niet op document: Horizon bindt al zijn on:-handlers
 * met een capture-listener op document (assets/component.js). Binnen
 * dezelfde fase op hetzelfde doel wint registratievolgorde, en Horizon laadt
 * eerder dan wij. Maar de capture-fase daalt af van window naar document,
 * dus een window-listener komt gegarandeerd eerst. (pointerenter/-leave
 * bubbelen niet; de capture-fase loopt wel.)
 *
 * Belangrijk: dit draait BOVENOP Horizon en raakt geen themabestand aan.
 * Herkent het de structuur niet, dan doet het niets en gedraagt het menu
 * zich als vanouds. Toetsenbordfocus blijft onaangeroerd (alleen
 * pointer-events). Nooit een kapot menu.
 */
(() => {
  const GENADE_MS = 280; // sluiten: hoe lang een uitstapje vergeven wordt
  const OPEN_MS = 110; //   openen: hoe lang de muis moet rusten
  const ITEM = '.menu-list__list-item';
  const OPEN_PANEEL = '.menu-list__submenu[data-active]';
  const SUBMENU = '.menu-list__submenu';

  /** @type {Map<Element, number>} lopende sluittimers per menu-item */
  const sluittimers = new Map();
  /** @type {Map<Element, number>} lopende opentimers per menu-item */
  const opentimers = new Map();

  const annuleerSluiten = (item) => {
    const t = sluittimers.get(item);
    if (t !== undefined) {
      clearTimeout(t);
      sluittimers.delete(item);
    }
  };

  const annuleerOpenen = (item) => {
    const t = opentimers.get(item);
    if (t !== undefined) {
      clearTimeout(t);
      opentimers.delete(item);
    }
  };

  const stuurLeave = (item, pointerType) => {
    const weg = new PointerEvent('pointerleave', {
      bubbles: false,
      pointerType: pointerType || 'mouse',
    });
    weg.__erwalDoorlaten = true;
    item.dispatchEvent(weg);
  };

  /* Alle hangende sluit-events NU afleveren (behalve op `behalve`), zodat
     een verse opening nooit door een nagekomen leave wordt ingehaald. */
  const spoelSluiters = (behalve, pointerType) => {
    for (const [item] of Array.from(sluittimers)) {
      annuleerSluiten(item);
      if (item !== behalve) stuurLeave(item, pointerType);
    }
  };

  // ---- openen uitstellen ------------------------------------------------
  window.addEventListener(
    'pointerenter',
    (e) => {
      if (e.__erwalDoorlaten) return;

      const item = e.target;
      if (!(item instanceof Element) || !item.matches(ITEM)) return;
      // alleen items met een uitklappaneel; gewone links (Contact) meteen
      if (!item.querySelector(':scope > ' + SUBMENU)) return;
      // staat dit paneel al open (muis terug vanaf het paneel): de enter
      // hoeft Horizon niet opnieuw te bereiken, wel de sluittimer weg
      if (item.querySelector(':scope > ' + OPEN_PANEEL)) {
        e.stopImmediatePropagation();
        annuleerSluiten(item);
        return;
      }

      e.stopImmediatePropagation();
      annuleerOpenen(item);
      opentimers.set(
        item,
        window.setTimeout(() => {
          opentimers.delete(item);
          // muis moet er nog steeds op staan — strijken telt niet
          if (!item.matches(':hover')) return;
          // eerst nette afsluiting van al het oude, dán openen
          spoelSluiters(item, e.pointerType);
          const binnen = new PointerEvent('pointerenter', {
            bubbles: false,
            pointerType: e.pointerType || 'mouse',
          });
          binnen.__erwalDoorlaten = true;
          item.dispatchEvent(binnen);
        }, OPEN_MS)
      );
    },
    true
  );

  // ---- sluiten uitstellen -----------------------------------------------
  window.addEventListener(
    'pointerleave',
    (e) => {
      // door onszelf opnieuw afgevuurd: doorlaten naar Horizon
      if (e.__erwalDoorlaten) return;

      const item = e.target;
      if (!(item instanceof Element) || !item.matches(ITEM)) return;

      // weg voordat het paneel openging: alleen de opentimer annuleren
      annuleerOpenen(item);

      // alleen uitstellen als er echt een paneel openstaat
      if (!item.querySelector(':scope > ' + OPEN_PANEEL)) return;

      e.stopImmediatePropagation();
      annuleerSluiten(item);
      sluittimers.set(
        item,
        window.setTimeout(() => {
          sluittimers.delete(item);
          // staat de muis inmiddels weer op het item of het paneel, dan niets
          if (item.matches(':hover')) return;
          stuurLeave(item, e.pointerType);
        }, GENADE_MS)
      );
    },
    true
  );

  // terug op het item (of op het paneel erin) = sluiten annuleren
  document.addEventListener(
    'pointerover',
    (e) => {
      if (!(e.target instanceof Element)) return;
      const item = e.target.closest(ITEM);
      if (item) annuleerSluiten(item);
    },
    true
  );
})();
